import React, { useEffect, useMemo, useState } from "react";
import { toast, Toaster } from "react-hot-toast";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import UserProfileButton from "components/shared/UserProfileButton/UserProfileButton";
import { Autocomplete, Box, CircularProgress, TextField, Tooltip } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useGetActiveOrganizationsQuery } from "state/rtkQuery/org/orgQueries";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useUpdateUserMembershipBulkMutation, useUpdateUserOrgStatusMutation } from "state/rtkQuery/user/userMutations";
import {
  useGetCostCenterUsersQuery,
  useLazyGetUserQuery,
  useGetUsersByOrgAndStatusQuery,
} from "state/rtkQuery/user/userQueries";

import { useCurrentUserRoleNames, usePermission } from "components/RequirePermission/useAccessPermissions";
import { useUserCapabilities, useCopilotUsageCapabilities } from "components/RequirePermission/useCapabilities";
import { useEffectiveOrgRole } from "components/RequirePermission/useEffectiveOrgRole";
import { isCrossOrgAdminRole } from "components/RequirePermission/RequireEcrOpsRole";
import {
  EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW,
  EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW,
} from "utils/constants";
import { useDebounce } from "utils/useDebounce";

import dayjs from "dayjs";

import ChangeCostCenterModal from "./ChangeCostCenterModal";
import successGIF from "./cost-center-success.gif";

import AvatarRandomColor from "../ReusableDataGrid/AvatarRandomColor";
import ReusableActions from "../ReusableDataGrid/ReusableActions";
import ReusableDataGrid from "../ReusableDataGrid/ReusableDataGrid";
import { DEFAULT_PAGE_SIZE } from "components/shared/ExpandableGrid/PageSizeSelect";
import UserDetailModal from "../../../admin/components/CopilotDashboard/UserDetailModal";
import { getPresetRange } from "../../../admin/components/CopilotDashboard/datePresets";
import { FilterChips, FilterPanel } from "components/shared/FilterPanel/FilterPanel";
import { useFilterPanel } from "components/shared/FilterPanel/useFilterPanel";
import { useTabSortFilter } from "../hooks/useTabSortFilter";

// The Copilot usage popup opened from the GitHub icon always shows the
// current month — same range "This Month" resolves to on the Copilot
// Dashboard. To see other date ranges, admins go to that dashboard directly.
// Computed fresh on each open rather than once at module load, so a
// long-lived session doesn't get stuck showing a stale month.
const getCurrentMonthRange = () => {
  const { from, to } = getPresetRange("this_month");
  return { from: from.format("YYYY-MM-DD"), to: to.format("YYYY-MM-DD") };
};

const JoinedTab = ({ orgName }) => {
  const [columns, setColumns] = useState([]);
  const [updatedRows, setUpdatedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [query, setQuery] = useState("");
  const { orgId } = useParams();
  const [usersCostCenters, setUsersCostCenters] = useState(null);
  const [emailTruncateLimit, setEmailTruncateLimit] = useState(
    EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW
  );
  const [selectedUser, setSelectedUser] = useState(null);
  const [newCostCenter, setNewCostCenter] = useState({ id: "", name: "" });
  const [oldCostCenter, setOldCostCenter] = useState({ id: "", name: "" });
  const [open, setOpen] = useState(false);
  const [selectedUserDetails, setSelectedUserDetails] = useState(null);
  const [isUserDetailsLoading, setIsUserDetailsLoading] = useState(false);
  const [userDetailsError, setUserDetailsError] = useState("");
  const [selectedCopilotUser, setSelectedCopilotUser] = useState(null);
  const [triggerGetUser] = useLazyGetUserQuery();

  // Real, always-fresh RBAC data (GET /user/organizations) instead of the old
  // state.auth.permissions snapshot. isOrgOwner drives edit-level UI (Cost
  // Center column, Actions column, checkboxSelection, wide layout); the
  // Approver/Leadership-level view uses the narrower isOrgAdminView (no
  // organization:id:update). Leadership is org-scoped (isOrgLeadership), not
  // part of either global override — see isCrossOrgAdminRole. Neither
  // hasGlobalEditAccess nor hasGlobalViewAccess is trusted on its own: /user/roles'
  // role_name[] is a flat union of every role the user holds anywhere, including
  // per-org roles (e.g. OrganizationOwner on a DIFFERENT org) — their
  // permissions leak into the same permission set, so isCrossOrgAdminRole
  // confirms it's actually one of the 2 real global roles before trusting it.
  // OperationsUser is org-scoped like Leadership (see isCrossOrgAdminRole,
  // which deliberately excludes it), but gets full OrganizationOwner-equivalent
  // WRITE access on the orgs it's actually assigned to — folded into
  // isOwnerView, not just isOrgAdminView, per that decision.
  const { isOwner: isOrgOwner, isApprover: isOrgApprover, isLeadership: isOrgLeadership, isOperationsUser: isOrgOperationsUser, isLoading: isOrgRoleLoading } = useEffectiveOrgRole(orgId);
  const hasGlobalEditAccess = usePermission("organization:id:update");
  const hasGlobalViewAccess = usePermission("organization:read");
  const roleNames = useCurrentUserRoleNames();
  const isCrossOrgAdmin = isCrossOrgAdminRole(roleNames);
  const isOwnerView = isOrgOwner || isOrgOperationsUser || (hasGlobalEditAccess && isCrossOrgAdmin);
  const isOrgAdminView =
    isOwnerView || isOrgApprover || isOrgLeadership || (hasGlobalViewAccess && isCrossOrgAdmin);

  // Actions column + bulk-remove checkboxes are gated on the capability to
  // update a user's org membership (user:id:update) — useUserCapabilities is
  // already scoped to THIS org's role (see useEffectiveOrgPermission): it
  // checks the specific role useEffectiveOrgRole(orgId) reports for this org
  // against THAT role's own permissions, not the user's global cross-org
  // union, so an Owner assignment on a DIFFERENT org can't leak in here.
  const { canUpdateUser, canViewCostCenter } = useUserCapabilities(orgId);
  const showActionsColumn = isOwnerView && canUpdateUser;
  const showCostCenterColumn = isOwnerView && canViewCostCenter;
  const canEditCostCenter = showCostCenterColumn && canUpdateUser;

  // Copilot column (GitHub icon -> usage popup) needs the same full
  // usage-read bundle as every other "view a user's Copilot usage" surface
  // in the app (BulkChangeBudgetModal, UlbRequestsTable) — also org-scoped.
  const {
    canViewUsage,
    canViewUsageById,
    canViewModelUsage,
    canViewUsageByCostCenter,
    canViewUsageByCostCenterId,
  } = useCopilotUsageCapabilities(orgId);
  const showCopilotColumn =
    isOrgAdminView &&
    canViewUsage && canViewUsageById && canViewModelUsage && canViewUsageByCostCenter && canViewUsageByCostCenterId;

  useEffect(() => {
    if (!isOwnerView && isOrgAdminView) {
      setEmailTruncateLimit(
        EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_APPROVER_VIEW
      );
    }
  }, [isOwnerView, isOrgAdminView]);

  const {
    data: platformData,
    isFetching: isPlatformDataLoading,
    isError: isPlatformDataError,
  } = useGetPlatformsQuery();

  // RTK Query to get Cost Center List

  const {
    data: costCenterListData,
    isFetching: isCostCenterListDataLoading,
    isSuccess: isCostCenterListDataSuccess,
    isError: isCostCenterListDataError,
  } = useGetCostCenterUsersQuery(
    {
      platformId: platformData?._id,
    },
    { skip: !platformData?._id }
  );

  const { data: allOrgs, isSuccess: isOrgsSuccess } =
    useGetActiveOrganizationsQuery();

  // The filters offered on this tab — all switched-on filters apply together (the API ANDs them).
  const filterFields = useMemo(() => {
    const fields = [
      { key: "email", label: "Email", type: "text" },
      { key: "first_name", label: "First Name", type: "text" },
      { key: "last_name", label: "Last Name", type: "text" },
      { key: "changed_date", label: "Date of Joining", type: "date" },
      { key: "changed_by", label: "Approved By", type: "text" },
    ];
    // Whether they hold a Copilot license in this org — only for people who can see Copilot data.
    if (showCopilotColumn) {
      fields.push({
        key: "has_copilot_license",
        label: "Copilot License",
        type: "select",
        options: [
          { value: "true", label: "Has a license" },
          { value: "false", label: "No license" },
        ],
      });
    }
    if (isOwnerView) {
      fields.push({
        key: "cost_center_id",
        label: "Cost Center",
        type: "autocomplete",
        options: (Array.isArray(costCenterListData?.cost_centers) ? costCenterListData.cost_centers : []).map((cc) => ({ value: cc._id, label: cc.name })),
      });
    }
    return fields;
  }, [isOwnerView, showCopilotColumn, costCenterListData]);

  const { filters: activeFilters, setFilters, query: filterQuery } = useFilterPanel(filterFields, () => setCurrentPage(1));

  // Use the sorting and filtering hook
  const {
    sortBy,
    sortOrder,
    nameSortMenuAnchor,
    handleSortClick,
    handleDirectSort,
    handleNameSortMenuOpen,
    renderNameSortMenu,
    renderNameSortIcon,
    renderSortIcon,
  } = useTabSortFilter([], {}, setCurrentPage);

  //RTK Query to get users by org and status
  const {
    data: joinedUserListData,
    isFetching: isJoinedUserListDataLoading,
    isSuccess: isJoinedUserListDataSuccess,
    isError: isJoinedUserListDataError,
  } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Joined",
      platformId: platformData?._id,
      page: currentPage,
      perPage,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
      extraFilters: filterQuery,
    },
    { skip: !platformData?._id, refetchOnMountOrArgChange: true }
  );

  // Set datagrid rows
  // Combines both joined and remove requested users into a single list
  useEffect(() => {
    if (isJoinedUserListDataSuccess && joinedUserListData?.users) {
      // User cost centers need to be set in a separate state to allow for dynamic updates on the table by org owners and approvers
      joinedUserListData?.users?.map((user) => {
        setUsersCostCenters((prev) => ({
          ...prev,
          [user.id]: user?.costCenter?.length > 0 ? user.costCenter : "",
        }));
      });
      setUpdatedRows(joinedUserListData?.users);
    }
  }, [joinedUserListData, isJoinedUserListDataSuccess]);

  // Bulk mutation
  const [
    updateUserMembershipBulk,
    { isLoading: isUpdateUserMembershipBulkLoading },
  ] = useUpdateUserMembershipBulkMutation();

  const userEmail = useSelector((state) => state.auth.userAccount?.username);

  // Bulk handler
  const handleBulkAction = async (userIds, rejectReason, otherReason) => {
    try {
      const bulkPayload = {
        platforms: {
          [platformData?._id]: {
            organizations: {
              [orgId]: {
                membership_status: "Remove-Requested",
                reason: rejectReason === "Other" ? otherReason : rejectReason,
                changed_by: userEmail?.toLowerCase(),
              },
            },
          },
        },
        user_ids: userIds,
      };
      await updateUserMembershipBulk({
        body: bulkPayload,
        invalidateStatuses: ["Joined", "Remove-Requested"],
      });
    } catch (error) {
      // Handle error
    }
  };

  // Single user action with status check
  // Single user action with status check - use single-user mutation when acting on one user
  const [updateUserOrgStatus] = useUpdateUserOrgStatusMutation();

  const singleUserAction = (params) => async (rejectReason, otherReason) => {
    if (params.row.status === "joined") {
      try {
        const singlePayload = {
          platforms: {
            [platformData?._id]: {
              organizations: {
                [orgId]: {
                  membership_status: "Remove-Requested",
                  reason: rejectReason === "Other" ? otherReason : rejectReason,
                  changed_by: userEmail?.toLowerCase(),
                },
              },
            },
          },
        };

        await updateUserOrgStatus({
          userId: params.row.id,
          body: singlePayload,
          attribute: "membership_status",
        }).unwrap();
      } catch (err) {
        // fallback to bulk handler
        handleBulkAction([params.row.id], rejectReason, otherReason);
      }
    }
  };

  // Multi user action (no status check, as you should filter selectedRows before calling)
  const multiUserAction = (userIds, rejectReason, otherReason) => {
    handleBulkAction(userIds, rejectReason, otherReason);
  };

  const orgIdToName = useMemo(() => {
    if (!isOrgsSuccess || !Array.isArray(allOrgs)) {
      return {};
    }

    return allOrgs.reduce((acc, org) => {
      acc[org._id] = org.name;
      return acc;
    }, {});
  }, [allOrgs, isOrgsSuccess]);

  const costCenterById = useMemo(() => {
    const costCenters = costCenterListData?.cost_centers;

    if (!isCostCenterListDataSuccess || !Array.isArray(costCenters)) {
      return {};
    }

    return costCenters.reduce((acc, costCenter) => {
      acc[costCenter._id] = costCenter.name;
      return acc;
    }, {});
  }, [costCenterListData, isCostCenterListDataSuccess]);

  const handleOpenUserDetails = async (row) => {
    // The merged modal opens straight away on the Copilot data; the profile fills in when it arrives.
    setSelectedCopilotUser(row);
    setSelectedUserDetails(null);
    setIsUserDetailsLoading(true);
    setUserDetailsError("");

    try {
      const result = await triggerGetUser({
        email: row.email,
        platformId: platformData?._id,
      });
      const users = Array.isArray(result?.data) ? result.data : [];
      const exactMatch = users.find((user) => user._id === row.id);

      setSelectedUserDetails(exactMatch || users[0] || null);

      if (!users.length) {
        setUserDetailsError("Could not find user details for this user.");
      }
    } catch (error) {
      setSelectedUserDetails(null);
      setUserDetailsError("Failed to load user details. Please try again.");
    } finally {
      setIsUserDetailsLoading(false);
    }
  };

  // Function to execute when cost center dropdown is changed
  const updateCostCenter = (userId, platformId, costCenterID) => {
    const loadingToast = toast.loading("Updating cost center for user", {
      style: {
        minWidth: "300px",
      },
    });

    updateUserOrgStatus({
      userId: userId,
      body: {
        platforms: {
          [platformId]: {
            cost_center_id: costCenterID,
          },
        },
      },
      attribute: "cost_center_id",
    })
      .unwrap()
      .then(() => {
        toast.dismiss(loadingToast);
        toast(
          <div>
            <img
              src={successGIF}
              height={"40px"}
              width={"40px"}
              alt="Success Icon"
            />
            Cost center updated successfully!
          </div>,
          {
            duration: 3500,
            style: {
              minWidth: "fit-content",
            },
          }
        );
      })
      .catch((error) => {
        toast.dismiss(loadingToast);
        // toast.error(`Failed to update cost center: ${error.toString()}`, {
        toast.error(`Failed to update cost center. Please try again later.`, {
          duration: 6000,
          style: {
            minWidth: "fit-content",
          },
        });
        console.error("Failed to update cost center:", error);
      });
  };

  // Handle cost center dropdown on-change evebt
  const handleCostCenterChange = (user, newCostCenterName) => {
    const newCostCenter = costCenterListData?.cost_centers?.find(
      (costCenter) => costCenter.name === newCostCenterName
    );

    const oldCostCenterId = usersCostCenters[user.id] || "";
    const oldCostCenter = costCenterListData?.cost_centers?.find(
      (costCenter) => costCenter._id === oldCostCenterId
    );

    const oldCostCenterName = oldCostCenter
      ? oldCostCenter.name
      : "Not assigned";

    setSelectedUser(user);
    setNewCostCenter({
      id: newCostCenter?._id || "",
      name: newCostCenterName,
    });
    setOldCostCenter({
      id: oldCostCenterId,
      name: oldCostCenterName,
    });

    setOpen(true);
  };

  const handleCostCenterModalConfirmationChange = () => {
    setUsersCostCenters((prev) => ({
      ...prev,
      [selectedUser?.id]: newCostCenter,
    }));
    setOpen(false);
    updateCostCenter(selectedUser.id, platformData?._id, newCostCenter.id);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Set columns
  useEffect(() => {
    if (updatedRows && !isOrgRoleLoading) {
      setColumns([
        {
          field: "profile",
          headerName: "Profile",
          headerAlign: "center",
          align: "center",
          flex: 0.4,
          minWidth: 80,
          sortable: false,
          filterable: false,
          // One icon for the profile and the Copilot usage; its badge says whether they hold a license.
          renderCell: (params) => (
            <UserProfileButton
              userLabel={params.row.email}
              onClick={() => handleOpenUserDetails(params.row)}
              licensedOrgNames={
                showCopilotColumn
                  ? (params.row.copilotBudget?.licensed_organizations ?? []).map((id) => orgIdToName[id] || id)
                  : undefined
              }
            />
          ),
        },
        {
          field: "name",
          headerName: `Showing ${(currentPage - 1) * perPage + 1} - ${(currentPage - 1) * perPage + updatedRows?.length
            } of ${joinedUserListData?.total || 0} Users`,
          headerClassName:
            isOwnerView
              ? "history-tab-totalCount-header"
              : "history-tab-totalCount-header-approver-view",
          headerAlign: "left",
          align: "left",
          flex: 2,
          minWidth: isOwnerView ? 270 : 350,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className={isOwnerView ? "history-tab-totalCount-header" : "history-tab-totalCount-header-approver-view"}
                style={{ 
                  overflow: "hidden", 
                  textOverflow: "ellipsis", 
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0
                }}
              >
                {`Showing ${(currentPage - 1) * perPage + 1} - ${(currentPage - 1) * perPage + updatedRows?.length} of ${joinedUserListData?.total || 0} Users`}
              </span>
              {renderNameSortIcon()}
              {renderNameSortMenu(setCurrentPage)}
            </Box>
          ),
          renderCell: (params) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                paddingLeft:
                  isOwnerView ? "0px" : "30px",
              }}
            >
              <AvatarRandomColor
                firstName={params.row.firstName}
                lastName={params.row.lastName}
                color={params.row.color}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "20px",
                }}
              >
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.name}</span>
                  }
                  placement="top-start"
                  arrow
                >
                  <span
                    className="table-history-userEmailJoined-cell"
                    style={{ fontWeight: "600", fontSize: "14px" }}
                  >
                    {params.row.name}
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.email}</span>
                  }
                  arrow
                  placement="bottom-start"
                >
                  {params.row.email.length > emailTruncateLimit ? (
                    <div>
                      <span
                        className="table-cell-trucate-reason"
                        style={{ paddingLeft: "0", fontSize: "13px" }}
                      >
                        {params.row.email?.slice(0, emailTruncateLimit) + "..."}
                      </span>{" "}
                    </div>
                  ) : (
                    <span style={{ fontSize: "13px" }}>{params.row.email}</span>
                  )}
                </Tooltip>
              </div>
            </div>
          ),
        },
        {
          field: "JoinedDate",
          headerName: "Date of Joining",
          headerClassName: "date-joined-table-header",
          headerAlign: "center",
          align: "center",
          flex: 1,
          minWidth: isOwnerView ? 100 : 240,
          valueGetter: (params) => {
            // Return the date as a Date object for proper sorting
            return new Date(params.row.date);
          },
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="date-joined-table-header date-joined-title"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Date of Joining
              </span>
              {renderSortIcon("changed_date", "Date of Joining")}
            </Box>
          ),
          renderCell: (params) => (
            <span className="history-tab-joinedDate-cell ">
              {params.row.date}
            </span>
          ),
        },
        {
          field: "approvedBy",
          headerName: "Approved By",
          headerClassName: "orgName-column-header",
          headerAlign: "center",
          align: "center",
          flex: 1.5,
          minWidth: isOwnerView ? 200 : 350,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="orgName-column-header approved-by-title"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Approved By
              </span>
              {renderSortIcon("changed_by", "Approved By")}
            </Box>
          ),
          renderCell: (params) => (
            <Tooltip
              title={<p style={{ fontSize: "12px" }}>{params.row.changedBy}</p>}
            >
              <span className="table-cell-history">{params.row.changedBy}</span>
            </Tooltip>
          ),
        },
        //Cost Center field
        showCostCenterColumn && {
          field: "costCenter",
          headerName: "Cost Center",
          headerAlign: "center",
          align: "center",
          backgroundColor: "red",
          flex: 1.3,
          minWidth: 180,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span
                className="cost-center-title"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Cost Center
              </span>
              {renderSortIcon("cost_center", "Cost Center")}
            </Box>
          ),
          renderCell: (params) => {
            if (
              !usersCostCenters ||
              isCostCenterListDataLoading ||
              !isCostCenterListDataSuccess
            ) {
              return <CircularProgress size={24} />;
            }
            const userCostCenterId = usersCostCenters[params.row.id] || "";
            const costCenter = costCenterListData?.cost_centers?.find(
              (option) => option._id === userCostCenterId
            );
            const userCostCenterName = costCenter
              ? costCenter.name
              : "Select option";

            if (!canEditCostCenter) {
              return (
                <Tooltip title={userCostCenterName} placement="top">
                  <span>{userCostCenterName}</span>
                </Tooltip>
              );
            }

            return (
              <Tooltip title={userCostCenterName} placement="top">
                {/* The data grid treats keydown bubbling from a cell as grid shortcuts
                    (e.g. Ctrl+A selects every row) — keep typing inside the search box. */}
                <div
                  style={{ width: "100%" }}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <Autocomplete
                    id="costCenter"
                    size="small"
                    disableClearable
                    options={costCenterListData?.cost_centers ?? []}
                    getOptionLabel={(option) => option?.name ?? ""}
                    isOptionEqualToValue={(option, value) => option._id === value._id}
                    value={costCenter ?? null}
                    onChange={(event, newValue) =>
                      handleCostCenterChange(params.row, newValue?.name)
                    }
                    style={{ width: "100%" }}
                    // Names are long — let the list be wider than the cell.
                    componentsProps={{
                      popper: { style: { width: "fit-content", minWidth: 260 } },
                    }}
                    renderInput={(inputParams) => (
                      <TextField
                        {...inputParams}
                        placeholder="Select option"
                        variant="outlined"
                        sx={{ "& .MuiInputBase-root": { fontSize: "inherit" } }}
                      />
                    )}
                  />
                </div>
              </Tooltip>
            );
          },
        },
        showActionsColumn && {
          field: "action",
          headerAlign: "center",
          headerName: "Actions",
          align: "center",
          flex: 1,
          minWidth: isOwnerView ? 130 : 0,
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <ReusableActions
              params={params}
              action={singleUserAction(params)}
            />
          ),
        },
      ].filter(Boolean));
    }
  }, [
    updatedRows,
    isOrgRoleLoading,
    isOwnerView,
    showActionsColumn,
    showCostCenterColumn,
    canEditCostCenter,
    showCopilotColumn,
    usersCostCenters,
    costCenterListData,
    isCostCenterListDataSuccess,
    currentPage,
    perPage,
    joinedUserListData?.total,
    emailTruncateLimit,
    platformData?._id,
    orgIdToName,
    sortBy,
    sortOrder,
    nameSortMenuAnchor,
    handleDirectSort,
  ]);

  const currentMonthRange = getCurrentMonthRange();

  return (
    <div>
      <UserDetailModal
        open={Boolean(selectedCopilotUser)}
        onClose={() => {
          setSelectedCopilotUser(null);
          setSelectedUserDetails(null);
          setUserDetailsError("");
        }}
        user={selectedCopilotUser}
        orgId={orgId}
        platformId={platformData?._id}
        fromDate={showCopilotColumn ? currentMonthRange.from : undefined}
        toDate={showCopilotColumn ? currentMonthRange.to : undefined}
        canViewUsage={showCopilotColumn}
        footerNote={showCopilotColumn ? "Showing current month usage only. Visit the Copilot Dashboard to view other date ranges." : undefined}
        profile={{
          user: selectedUserDetails,
          orgIdToName,
          costCenterById,
          isLoading: isUserDetailsLoading,
          error: userDetailsError,
        }}
      />
      <ReusableDataGrid
        columns={columns}
        rows={updatedRows}
        orgName={orgName}
        viewType={"Joined History"}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        recordsPerPage={perPage}
        onRecordsPerPageChange={(size) => {
          setPerPage(size);
          setCurrentPage(1);
        }}
        totalRecords={joinedUserListData?.total || 0}
        checkboxSelection={showActionsColumn}
        isLoading={
          isJoinedUserListDataLoading ||
          isUpdateUserMembershipBulkLoading ||
          isPlatformDataLoading ||
          isCostCenterListDataLoading
        }
        multiUserAction={multiUserAction}
        enableHorizontalScroll={true}
        customSearchControls={<FilterPanel fields={filterFields} filters={activeFilters} onFiltersChange={setFilters} height={31} />}
        customSearchInput={<></>}
        filterChips={<FilterChips fields={filterFields} filters={activeFilters} onFiltersChange={setFilters} />}
        tabDescription={
          "Users in Joined have been successfully added to the organization. These users will have access to the organization"
        }
      />
      <ChangeCostCenterModal
        show={open}
        close={handleClose}
        selectedUser={selectedUser}
        newCostCenter={newCostCenter}
        oldCostCenter={oldCostCenter}
        handleConfirmChange={handleCostCenterModalConfirmationChange}
      />
      <Toaster position="top-right" reverseOrder={false} />
    </div>
  );
};

export default JoinedTab;
